import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateFilledDataTemplateThunk,
} from "../../store/features/documentSlice";
import {
  updateMandatoryDataThunk,
  updateShipmentIssue,
  updateRequiredActions,
  updateShipmentStatus,
} from "../../store/features/shipmentsSlice";
import SuccessMessageBox from "../Notification/Success";
import { decrementImportsRequiringAttentionThunk } from "../../store/features/dashboardSlice";

const ExtractedMustInfo = ({ dataTemplate, threadId, documentId, shipmentId }) => {
  const dispatch = useDispatch();
  const [data, setData] = useState(dataTemplate); // Editable data state
  const [isComplete, setIsComplete] = useState(false); // Save button state

  useEffect(() => {
    setData(dataTemplate);
  }, [dataTemplate]);

  // Check if all fields are filled
  useEffect(() => {
    const checkCompletion = (obj) => {
      for (let key in obj) {
        if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
          if (!checkCompletion(obj[key])) return false;
        } else if (obj[key] === "" || obj[key] === "N/A") {
          return false;
        }
      }
      return true;
    };

    setIsComplete(checkCompletion(data));
  }, [data]);

  // Handle input changes
  const handleChange = (path, value) => {
    const keys = path.split(".");
    const updatedData = { ...data };
    let ref = updatedData;

    keys.forEach((key, idx) => {
      if (idx === keys.length - 1) {
        ref[key] = value;
      } else {
        ref[key] = { ...ref[key] };
        ref = ref[key];
      }
    });

    setData(updatedData);
  };

  // Recursive rendering of fields
  const renderFields = (obj, path = "") => {
    return Object.keys(obj).map((key) => {
      const currentPath = path ? `${path}.${key}` : key;
      const value = obj[key];

      if (typeof value === "object" && !Array.isArray(value)) {
        return (
          <div key={currentPath} className="mb-6 border p-4 rounded bg-gray-50">
            <h4 className="font-bold text-gray-700 mb-2">{key}</h4>
            <div className="grid grid-cols-2 gap-4 ml-4">
              {renderFields(value, currentPath)}
            </div>
          </div>
        );
      } else if (Array.isArray(value)) {
        return (
          <div key={currentPath} className="mb-6">
            <h4 className="font-bold text-gray-700 mb-2">{key}</h4>
            {value.map((item, idx) => (
              <div
                key={`${currentPath}[${idx}]`}
                className="mb-4 border p-4 rounded bg-gray-50"
              >
                <h5 className="font-bold text-gray-600">Item {idx + 1}</h5>
                <div className="grid grid-cols-2 gap-4 ml-4">
                  {renderFields(item, `${currentPath}[${idx}]`)}
                </div>
              </div>
            ))}
          </div>
        );
      } else {
        const isIncomplete = value === "" || value === "N/A";
        return (
          <div key={currentPath} className="mb-4">
            <label className="block text-gray-600 mb-1">{key}</label>
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(currentPath, e.target.value)}
              className={`w-full p-2 border rounded ${
                isIncomplete ? "border-red-500 bg-red-50" : "border-gray-300"
              }`}
              placeholder={`Enter ${key}`}
            />
          </div>
        );
      }
    });
  };
  const [showSuccessMessage, setShowSuccessMessage] = useState(false); // State for success message visibility
  const handleSave = async () => {
    if (threadId) {
      await dispatch(updateShipmentIssue({ shipmentId:threadId }));
      await dispatch(
        updateMandatoryDataThunk({ shipmentId: threadId, mandatoryData: data })
      );
    }
    console.log("Updated Data:", data); // Debugging output
    
  };

  // Handle Save Function
  const handleSaveClick = async () => {
    try {
        await handleSave(); // Call save function
        setShowSuccessMessage(true); // Show success popup

        // Automatically hide the message after 3 seconds
        setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
        console.error("Failed to save amends:", error);
        alert("Failed to save amends. Please try again.");
    }
};

  // Placeholder for View Draft Email functionality
  const handleViewDraftEmail = () => {
    console.log("View Draft Email clicked!");
    // Implement draft email preview or redirect here
  };

  // Close Case Functionality
  const handleCloseCase = async () => {
    const confirmClose = window.confirm("Are you sure you want to close this case?");
    if (!confirmClose) return;

    try {
      await dispatch(updateShipmentStatus({ shipmentId, status: "closed" })).unwrap();
      alert("Case closed successfully!");
      dispatch(decrementImportsRequiringAttentionThunk());
    } catch (error) {
      console.error("Failed to close the case:", error);
      alert("Failed to close the case. Please try again.");
    }
  };

  return (
        <div className="relative bg-bg p-1 flex border rounded-md border-border-document flex-col h-full">
            {/* Rendering Area */}
            <div className="flex-grow overflow-y-auto m-2 p-1 bg-bg">
                <h3 className="font-bold text-lg mb-4">Mandatory Info for Custom Clearance</h3>
                {/* Render data fields */}
                {renderFields(data)}
            </div>

            {/* Fixed Footer Area (OUTSIDE Scrolling Section) */}
            <div className="flex justify-end items-center pb-2 pr-2 bg-white">
                {/* Save Amends Button with Tooltip */}
                <div className="relative group">

                    <button
                        onClick={handleSaveClick} // Updated to use handleSaveClick
                        disabled={!isComplete}
                        className={`px-4 p-2 text-logo rounded-full transition-all ${
                            isComplete
                                ? 'bg-bg border border-logo text-logo hover:bg-logo hover:text-white'
                                : 'bg-bg border border-logo cursor-not-allowed'
                        }`}
                    >
                       Save Amends
                    </button>

                    {/* Tooltip - Appears only when button is disabled */}
                    {!isComplete && (
                        <div
                            className="fixed z-50 pb-4 p-2 text-sm text-white bg-gray-800 rounded-md shadow-lg hidden group-hover:block"
                            style={{
                                top: 'calc(100% - 150px)', // Adjust this to position it above the button
                                right: '50px', // Keeps it aligned to the right corner
                                width: '300px',
                            }}
                        >
                            Please fill all required fields.
                            If information is unavailable, proceed to send the draft email.
                        </div>
                    )}
                </div>
            </div>

            {/* Success Message Popup */}
            {showSuccessMessage && (
                <SuccessMessageBox
                  message="Mandatory info successfully updated"
                />
            )}
        </div>
  );
};

export default ExtractedMustInfo;
